import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, DatePicker, Drawer, Form, Input, Modal, Select, Space, Statistic, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { del, get, patch } from '../../lib/request'
import {
  APPLICATION_STATUS_LABELS,
  BenefitPlan,
  IDENTITY_LABELS,
  SHOP_NAME_MAX_LEN,
  Merchant,
  PageData,
  ReferrerDetail,
  ReferrerItem,
  RewardType,
  errMsg,
  fmtMoney,
  fmtTime,
} from '../../lib/api'
import { DocumentUploadList, isFallbackPlan } from '../BenefitConfig/shared'
import { useAuthStore } from '../../stores/auth'
import ExportModal, { ExportColumn } from './ExportModal'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

/**
 * P-PROJ-16FA993B0823（2026-10-22）：DELETE /admin/referrers/:id 成功响应 data 形状，
 * 逐字对齐后端 services/referrer-deletion.ts::ReferrerDeletionResult（均为 number）。
 */
interface ReferrerDeletionResult {
  invalidatedLeads: number
  deletedCards: number
  deletedApplications: number
  deletedChangeRequests: number
  deletedAnchors: number
  frozenRewards: number
}

/** 删除接口 403/404/409 的友好提示；其它错误回落后端 message。 */
const DELETE_ERROR_MESSAGES: Record<number, string> = {
  403: '无权限，仅超级管理员可删除',
  404: '推荐人不存在或已删除',
  409: '该推荐人已被删除',
}

const deleteErrMsg = (err: unknown): string => {
  const resp = (err as { response?: { status?: number; data?: { code?: number } } })?.response
  const code = resp?.data?.code ?? resp?.status
  return (code != null && DELETE_ERROR_MESSAGES[code]) || errMsg(err, '删除失败，请稍后重试')
}

/**
 * thankyou_v1（批次 2）编辑抽屉的身份选项。
 * 枚举取值逐字对齐后端 constants.ts::VALID_IDENTITIES（含项目商家 merchant）；
 * 中文按设计稿 referrer-edit-drawer.html（租户/业主/员工/社会）。列表与详情的「身份」列仍用
 * 既有 IDENTITY_LABELS 口径，不改动既有文案。
 */
const IDENTITY_OPTIONS = [
  { value: 'tenant', label: '租户' },
  { value: 'owner', label: '业主' },
  { value: 'employee', label: '员工' },
  { value: 'social', label: '社会' },
  { value: 'boss', label: '老板' },
  { value: 'merchant', label: '项目商家' },
]

interface ReferrerEditFormValue {
  name: string
  phone: string
  identity: string
  shopName?: string | null
  unit?: string | null
  tags?: string | null
  rewardTypeId?: number | null
  /** referrer_multi_plan：多业态绑定方案（每个业态至多一个；提交时同步 rewardPlanId = ids[0] ?? null） */
  rewardPlanIds?: number[]
  /** thankyou_v2（批次 8.2）扩 7 字段（Contract Pack 逐字） */
  merchantId?: number | null
  couponUsageNote?: string | null
  agreementNo?: string | null
  qualificationFiles?: string[]
  qualificationValidUntil?: dayjs.Dayjs | null
}

/** 礼遇交付责任方（需求 4.5）：入驻商家固定「招商运营发放」，前端只读不可改 */
const DELIVERY_RESPONSIBLE_TEXT = '招商运营发放'

/** 入驻商家类型的后端 code（Contract Pack：code='merchant' 时协议编号+商户+券说明必填） */
const MERCHANT_TYPE_CODE = 'merchant'

/**
 * qualificationFiles 归一化为 URL 数组。
 * 后端 DB 列是逗号分隔 TEXT（GET 返回字符串），前端表单态是数组；
 * 读取端统一经此函数还原，兼容两种运行时形态（字符串 / 数组 / null）。
 */
const toFiles = (v?: string | string[] | null): string[] => {
  if (Array.isArray(v)) return v.filter(Boolean)
  if (typeof v === 'string') return v.split(',').map((s) => s.trim()).filter(Boolean)
  return []
}

/**
 * referrer_multi_plan（2026-09-28）：推荐人绑定方案 id 列表（对齐后端 Contract Pack toPlanIds）。
 * rewardPlanIds 为非空整数数组则用之；否则回落旧列 rewardPlanId（过渡期旧数据）。
 */
const toPlanIds = (r?: Pick<ReferrerItem, 'rewardPlanIds' | 'rewardPlanId'> | null): number[] => {
  const raw = r?.rewardPlanIds
  const ids = Array.isArray(raw) ? raw.filter((x) => Number.isInteger(x)) : []
  if (ids.length > 0) return ids
  return r?.rewardPlanId != null ? [r.rewardPlanId] : []
}

/** 方案所属业态键：null = 通用（通用方案自成一类参与同业态互斥）；方案未加载时 undefined（不参与互斥） */
const planFormatKey = (plans: BenefitPlan[], id: number): number | null | undefined => {
  const p = plans.find((x) => x.id === id)
  return p ? p.businessFormatId ?? null : undefined
}

/** 业态标签前缀：「【写字楼】」/「【通用】」 */
const formatPrefix = (p: BenefitPlan): string =>
  `【${p.businessFormat?.name ?? (p.businessFormatId == null ? '通用' : `业态#${p.businessFormatId}`)}】`

/**
 * 同业态互斥：本次新勾选的方案若与已选方案同业态，则替换掉旧的（新选优先）。
 * 作为 Form.Item normalize 使用（value = 新值，prev = 旧值）。
 */
const keepOnePerFormat = (next: number[] | undefined, prev: number[] | undefined, plans: BenefitPlan[]): number[] => {
  const prevIds = prev ?? []
  let result = next ?? []
  for (const added of result.filter((id) => !prevIds.includes(id))) {
    const key = planFormatKey(plans, added)
    if (key === undefined) continue
    result = result.filter((id) => id === added || planFormatKey(plans, id) !== key)
  }
  return result
}

/**
 * 未完成注册：小程序登录即建档 name=''，注册成功 name 必非空。
 * 与后端 admin/referrers.ts GET 的 registration 筛选（name: '' / name: { not: '' }）同一口径。
 */
const isUnregistered = (r: Pick<ReferrerItem, 'name'>): boolean => !r.name

/**
 * referrer_export（2026-11-04）：可排序列的点击循环 —— 首次点击倒序，之后倒序/顺序来回切换，
 * 不进入「取消排序」态。antd 默认 ['ascend','descend'] 从 descend 再点会取消排序，
 * 而取消分支回落的正是默认 createdAt/desc，于是「注册时间」列点击看起来毫无反应。
 */
const SORT_CYCLE: ('ascend' | 'descend')[] = ['descend', 'ascend', 'descend']

type RegistrationFilter = '' | 'registered' | 'unregistered'

/** referrer_share_stats：服务端排序字段（对齐后端 sortBy 白名单） */
type SortBy = 'createdAt' | 'cards' | 'opens'
type SortDir = 'desc' | 'asc'

/** antd Table onChange sorter.field → 后端 sortBy 映射 */
const SORTER_FIELD_TO_SORT_BY: Record<string, SortBy> = {
  createdAt: 'createdAt',
  cardCount: 'cards',
  openCount: 'opens',
}

/** antd Table onChange sorter.order → 后端 sortDir 映射 */
const ORDER_TO_SORT_DIR: Record<string, SortDir> = {
  ascend: 'asc',
  descend: 'desc',
}

const REGISTRATION_OPTIONS: { value: RegistrationFilter; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'registered', label: '已注册' },
  { value: 'unregistered', label: '未完成注册' },
]

/** 推荐人管理：列表 + 详情抽屉（贡献统计）+ 标签编辑 + 资料编辑（thankyou_v1 批次 2） */
const ReferrersPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEditTags = hasPermission(role, 'REFERRER_EDIT_TAGS')
  // thankyou_v1（批次 2）：资料编辑挂新键 REFERRER_EDIT（仅超管/管理员，后端同步鉴权）
  const canEdit = hasPermission(role, 'REFERRER_EDIT')
  // P-PROJ-16FA993B0823：删除推荐人数据仅超管可见（后端 requirePermission + handler 二次校验）
  const canDelete = hasPermission(role, 'REFERRER_DELETE')

  const [list, setList] = useState<ReferrerItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [keyword, setKeyword] = useState('')
  const [registration, setRegistration] = useState<RegistrationFilter>('')
  const [loading, setLoading] = useState(false)

  // referrer_share_stats：排序与 4 个新筛选状态
  const [sortBy, setSortBy] = useState<SortBy>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [identityFilter, setIdentityFilter] = useState('')
  const [rewardTypeIdFilter, setRewardTypeIdFilter] = useState<number | undefined>(undefined)
  const [rewardPlanIdFilter, setRewardPlanIdFilter] = useState<number | undefined>(undefined)
  // leasing_manager 无答谢类型/方案接口权限：拉取 403 时隐藏对应筛选下拉
  const [benefitFilterDenied, setBenefitFilterDenied] = useState(false)

  const [detail, setDetail] = useState<ReferrerDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const [tagsTarget, setTagsTarget] = useState<ReferrerItem | null>(null)
  const [tagsForm] = Form.useForm<{ tags: string }>()
  const [tagsSaving, setTagsSaving] = useState(false)

  // thankyou_v1（批次 2）：编辑资料抽屉（后端 PATCH /admin/referrers/:id 白名单字段）
  const [editTarget, setEditTarget] = useState<ReferrerItem | null>(null)
  const [editForm] = Form.useForm<ReferrerEditFormValue>()
  const [editSaving, setEditSaving] = useState(false)
  const [rewardTypes, setRewardTypes] = useState<RewardType[]>([])
  const [benefitPlans, setBenefitPlans] = useState<BenefitPlan[]>([])
  // thankyou_v2（批次 8.2）：关联商户下拉（GET /admin/merchants）
  const [merchants, setMerchants] = useState<Merchant[]>([])
  // referrer_export：导出弹窗
  const [exportOpen, setExportOpen] = useState(false)
  // 方案下拉按所选类型联动过滤（后端亦校验「绑定方案不属于该答谢类型」400）
  const watchTypeId = Form.useWatch('rewardTypeId', editForm)
  const watchIdentity = Form.useWatch('identity', editForm)

  // 当前筛选 + 排序参数（列表与导出共用同一份，保证导出顺序 = 列表顺序）
  const listFilterParams: Record<string, string | number> = {
    ...(keyword ? { keyword } : {}),
    ...(registration ? { registration } : {}),
    // referrer_share_stats：排序 + 新筛选参数透传
    sortBy,
    sortDir,
    ...(identityFilter ? { identity: identityFilter } : {}),
    ...(rewardTypeIdFilter != null ? { rewardTypeId: rewardTypeIdFilter } : {}),
    ...(rewardPlanIdFilter != null ? { rewardPlanId: rewardPlanIdFilter } : {}),
  }

  const fetchList = useCallback(() => {
    setLoading(true)
    get<PageData<ReferrerItem>>('/admin/referrers', {
      params: { page, pageSize: 20, ...listFilterParams },
    })
      .then((d) => {
        setList(d.items ?? [])
        setTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '推荐人列表加载失败')))
      .finally(() => setLoading(false))
  }, [page, keyword, registration, sortBy, sortDir, identityFilter, rewardTypeIdFilter, rewardPlanIdFilter])

  useEffect(() => { fetchList() }, [fetchList])

  // referrer_multi_plan：列表「绑定方案」列 / 详情需要 id → 名称映射；静默加载，失败回落 rewardPlan.name
  // referrer_share_stats：同时拉 benefit-types（答谢类型筛选下拉）；403 时降级（leasing_manager 无权限）
  useEffect(() => {
    get<BenefitPlan[]>('/admin/benefit-plans')
      .then((d) => setBenefitPlans(Array.isArray(d) ? d : []))
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 403) setBenefitFilterDenied(true)
      })
    get<RewardType[]>('/admin/benefit-types')
      .then((d) => setRewardTypes(Array.isArray(d) ? d : []))
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 403) setBenefitFilterDenied(true)
      })
  }, [])

  /** 多方案名称（「、」连接）：benefitPlans 映射；映射不到回落 rewardPlan.name / #id */
  const planNamesOf = (r: ReferrerItem): string =>
    toPlanIds(r)
      .map((id) =>
        benefitPlans.find((p) => p.id === id)?.name
        ?? (id === r.rewardPlan?.id ? r.rewardPlan?.name : undefined)
        ?? `#${id}`)
      .join('、') || (r.rewardPlan?.name ?? '')

  const openDetail = (r: ReferrerItem) => {
    setDetailOpen(true)
    setDetail(null)
    get<ReferrerDetail>(`/admin/referrers/${r.id}`)
      .then(setDetail)
      .catch((err) => message.warning(errMsg(err, '推荐人详情加载失败')))
  }

  const saveTags = async () => {
    if (!tagsTarget) return
    const v = await tagsForm.validateFields()
    setTagsSaving(true)
    try {
      await patch(`/admin/referrers/${tagsTarget.id}/tags`, { tags: v.tags })
      message.success('标签已更新')
      setTagsTarget(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '标签更新失败'))
    } finally {
      setTagsSaving(false)
    }
  }

  // 打开编辑抽屉时才拉类型/方案/商户（仅 SA/A 能看到按钮，避免无谓请求）
  const openEdit = (r: ReferrerItem) => {
    setEditTarget(r)
    editForm.setFieldsValue({
      name: r.name,
      phone: r.phone,
      identity: r.identity,
      shopName: r.shopName ?? undefined,
      unit: r.unit ?? undefined,
      tags: r.tags ?? undefined,
      rewardTypeId: r.rewardTypeId ?? undefined,
      rewardPlanIds: toPlanIds(r),
      // thankyou_v2 扩 7 字段回显（qualificationFiles 后端返回逗号分隔字符串，还原为数组）
      merchantId: r.merchantId ?? undefined,
      couponUsageNote: r.couponUsageNote ?? undefined,
      agreementNo: r.agreementNo ?? undefined,
      qualificationFiles: toFiles(r.qualificationFiles),
      qualificationValidUntil: r.qualificationValidUntil
        ? dayjs(String(r.qualificationValidUntil).slice(0, 10))
        : null,
    })
    get<RewardType[]>('/admin/benefit-types')
      .then((d) => setRewardTypes(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '答谢类型加载失败')))
    get<BenefitPlan[]>('/admin/benefit-plans')
      .then((d) => setBenefitPlans(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '答谢方案加载失败')))
    get<Merchant[] | { items: Merchant[] }>('/admin/merchants')
      .then((d) => setMerchants(Array.isArray(d) ? d : d.items ?? []))
      // 无 MERCHANT_MANAGE 的账号读商户列表可能 403：不阻断抽屉，仅降级提示
      .catch((err) => message.warning(errMsg(err, '商户列表加载失败')))
  }

  /** 当前编辑行所选类型的 code（决定入驻商家强制字段与资质强制） */
  const watchTypeCode = rewardTypes.find((t) => t.id === watchTypeId)?.code
  const isMerchantType = watchTypeCode === MERCHANT_TYPE_CODE
  /** 需资质校验类型（入驻商家 / 商会）：资质附件必传，不传不允许保存（后端 400 双保险） */
  const needQualification = !!rewardTypes.find((t) => t.id === watchTypeId)?.needQualification
    || isMerchantType
    || watchTypeCode === 'chamber'

  /**
   * referrer_multi_plan：该类型各业态的长期方案（effectiveTo=null 且 published），每个业态（含通用）取第一个。
   * benefitPlans 已按 sortOrder asc, id asc 返回，与后端 findFallbackPlansPerFormat 口径一致。
   */
  const findFallbackPlanIds = (typeId?: number | null): number[] => {
    if (typeId == null) return []
    const byFormat = new Map<number | null, number>()
    for (const p of benefitPlans) {
      if (p.rewardTypeId !== typeId || !isFallbackPlan(p)) continue
      const key = p.businessFormatId ?? null
      if (!byFormat.has(key)) byFormat.set(key, p.id)
    }
    return Array.from(byFormat.values())
  }

  /**
   * thankyou_v2（批次 8.2）：rewardTypeId 切换改 Modal.confirm 二次确认（需求 5.2）：
   * 确认后把绑定方案自动重置为新类型各业态长期方案全选（referrer_multi_plan；后端 PATCH 未传方案时亦同步重置）；
   * 取消则回滚下拉到原值（Select 受控于 form，故 setFieldValue 即可）。
   */
  const handleTypeChange = (nextTypeId?: number | null) => {
    const prevTypeId = editTarget?.rewardTypeId ?? undefined
    if (nextTypeId === prevTypeId) return
    Modal.confirm({
      title: '切换答谢类型？',
      content: '切换类型将自动勾选该类型各业态的长期方案；已发生的推荐按线索快照执行，不受影响。',
      okText: '确认切换',
      cancelText: '取消',
      onOk: () => {
        const fallbackIds = findFallbackPlanIds(nextTypeId)
        editForm.setFieldsValue({
          rewardTypeId: nextTypeId ?? undefined,
          rewardPlanIds: fallbackIds,
        })
        if (nextTypeId != null && fallbackIds.length === 0) {
          message.warning('该类型尚无长期方案，留空则按客户业态自动匹配')
        }
      },
      onCancel: () => {
        // 回滚：Select 已视觉变更，重置为原类型
        editForm.setFieldValue('rewardTypeId', prevTypeId)
      },
    })
  }

  /**
   * P-PROJ-16FA993B0823：删除推荐人数据（不可恢复）。沿用本页 Modal.confirm 二次确认风格；
   * 线索不删除只转无效，答谢停发（用户 2026-10-22 裁决）。onOk 返回 Promise，请求期间按钮 loading；
   * 失败时 message.error 提示后关闭弹窗（不 rethrow，避免 antd 未处理 rejection）。
   */
  const confirmDelete = (r: ReferrerItem) => {
    Modal.confirm({
      title: `删除推荐人「${r.name || r.rid}」？`,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>删除后将：</div>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>清空该推荐人的个人信息（姓名、手机号等）</li>
            <li>其未完成的线索转为无效（线索记录保留）</li>
            <li>未发放的答谢停发</li>
          </ul>
          <div style={{ marginTop: 8 }}>
            <Text type="danger" strong>此操作不可恢复。</Text>
          </div>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await del<ReferrerDeletionResult>(`/admin/referrers/${r.id}`)
          message.success(`已删除，已将 ${res?.invalidatedLeads ?? 0} 条线索转为无效`)
          if (detail?.id === r.id) setDetailOpen(false)
          fetchList()
        } catch (err: unknown) {
          message.error(deleteErrMsg(err))
          // 404/409 表示该行已不存在或已删除：刷新列表让它消失
          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 404 || status === 409) fetchList()
        }
      },
    })
  }

  const saveEdit = async () => {
    if (!editTarget) return
    const v = await editForm.validateFields()
    // 前端预拦截（后端 PATCH 为权威闸门，同文案 400）：
    // ① needQualification 类型资质附件必传；② 入驻商家协议编号+商户+券说明必填
    if (needQualification && (v.qualificationFiles ?? []).length === 0) {
      message.error('该答谢类型需资质校验：请先上传资质材料附件（图片 / PDF），否则不允许保存')
      return
    }
    if (isMerchantType && (!v.agreementNo?.trim() || v.merchantId == null || !v.couponUsageNote?.trim())) {
      message.error('入驻商家类型：关联商户 + 券使用说明 + 商家协议编号为必填（未签协议不允许绑定）')
      return
    }
    const planIds = v.rewardPlanIds ?? []
    setEditSaving(true)
    try {
      await patch(`/admin/referrers/${editTarget.id}`, {
        name: v.name.trim(),
        phone: v.phone,
        identity: v.identity,
        ...(v.identity === 'merchant' ? { shopName: v.shopName?.trim() || null } : {}),
        unit: v.unit?.trim() || null,
        tags: v.tags?.trim() || null,
        // 清空时传 null（后端白名单接受 null；联动校验方案归属）
        rewardTypeId: v.rewardTypeId ?? null,
        // referrer_multi_plan：rewardPlanIds 为准；过渡期双发 rewardPlanId = ids[0] ?? null 兼容旧后端
        rewardPlanIds: planIds,
        rewardPlanId: planIds[0] ?? null,
        // thankyou_v2 扩 7 字段（deliveryResponsible 只读，入驻商家固定「招商运营发放」）
        merchantId: v.merchantId ?? null,
        couponUsageNote: v.couponUsageNote?.trim() || null,
        agreementNo: v.agreementNo?.trim() || null,
        // 后端按「字符串或 null」校验 + DB 列是逗号分隔 TEXT：数组拼成逗号分隔字符串，空数组 → null
        qualificationFiles: (v.qualificationFiles ?? []).length
          ? (v.qualificationFiles ?? []).filter(Boolean).join(',')
          : null,
        qualificationValidUntil: v.qualificationValidUntil
          ? v.qualificationValidUntil.format('YYYY-MM-DD')
          : null,
        ...(isMerchantType ? { deliveryResponsible: DELIVERY_RESPONSIBLE_TEXT } : {}),
      })
      message.success('推荐人资料已更新')
      setEditTarget(null)
      fetchList()
    } catch (err: unknown) {
      // 后端文案原样透出：phone 无效 / 答谢类型已停用 / 绑定方案不属于该答谢类型 /
      // 资质附件缺失（needQualification）/ 入驻商家必填项缺失（400）
      message.error(errMsg(err, '保存失败'))
    } finally {
      setEditSaving(false)
    }
  }

  // 类型下拉：仅启用项（已发布），停用/草稿不开放新绑定；需资质校验类型标注
  const typeOptions = rewardTypes
    .filter((t) => t.status === 'published')
    .map((t) => ({
      value: t.id,
      label: [
        t.name,
        t.isDefault ? '（兜底）' : '',
        t.needQualification || t.code === MERCHANT_TYPE_CODE ? '［需资质］' : '',
      ].join(''),
    }))

  // 商户下拉（thankyou_v2）：仅在营项；当前绑定已停用时补一项避免回显裸 id
  const merchantOptions = (() => {
    const opts = merchants
      .filter((m) => m.status !== 'inactive')
      .map((m) => ({ value: m.id, label: m.name }))
    const cur = editTarget?.merchantId
    if (cur != null && !opts.some((o) => o.value === cur)) {
      const hit = merchants.find((m) => m.id === cur)
      opts.push({ value: cur, label: `${hit?.name ?? `#${cur}`}（已停用）` })
    }
    return opts
  })()

  // 方案下拉：按所选类型前端 filter（排除已停用，后端仅拒 offline）；草稿标注供辨别
  const planOptions = (() => {
    const filtered = benefitPlans.filter(
      (p) => p.status !== 'offline' && (watchTypeId == null || p.rewardTypeId === watchTypeId),
    )
    // referrer_multi_plan：标签加业态前缀「【写字楼】xxx」（null →「【通用】」）
    const opts = filtered.map((p) => ({
      value: p.id,
      label: `${formatPrefix(p)}${p.status === 'draft' ? `${p.name}（草稿）` : p.name}`,
    }))
    // 已绑定方案不在可选集（如已停用）时逐个补回显，避免裸 id；兜底方案标注
    for (const cur of toPlanIds(editTarget)) {
      if (opts.some((o) => o.value === cur)) continue
      const hit = benefitPlans.find((p) => p.id === cur)
      const name = hit ? `${formatPrefix(hit)}${hit.name}`
        : (cur === editTarget?.rewardPlan?.id ? editTarget?.rewardPlan?.name : undefined) ?? `#${cur}`
      opts.push({ value: cur, label: `${name}（已停用）` })
    }
    return opts.map((o) => {
      const p = filtered.find((x) => x.id === o.value)
      return isFallbackPlan(p) ? { ...o, label: `${o.label}［兜底］` } : o
    })
  })()

  const renderTags = (tags?: string | null) =>
    tags ? (
      <Space size={4} wrap>
        {tags.split(',').filter(Boolean).map((t) => (
          <Tag key={t} color="blue">{t.trim()}</Tag>
        ))}
      </Space>
    ) : (
      <Text type="secondary">—</Text>
    )

  /** 重置全部筛选 + 排序回默认 */
  const resetFilters = () => {
    setKeyword('')
    setRegistration('')
    setIdentityFilter('')
    setRewardTypeIdFilter(undefined)
    setRewardPlanIdFilter(undefined)
    setSortBy('createdAt')
    setSortDir('desc')
    setPage(1)
  }

  // 答谢类型筛选下拉选项（复用挂载时拉取的 rewardTypes，仅启用项）
  const rewardTypeFilterOptions = rewardTypes
    .filter((t) => t.status === 'published')
    .map((t) => ({ value: t.id, label: t.name }))

  // 答谢方案筛选下拉选项（复用挂载时拉取的 benefitPlans，排除停用；showSearch 由 Select 属性控制）
  const benefitPlanFilterOptions = benefitPlans
    .filter((p) => p.status !== 'offline')
    .map((p) => ({ value: p.id, label: `${formatPrefix(p)}${p.name}` }))

  const columns: ColumnsType<ReferrerItem> = [
    { title: '推荐码', dataIndex: 'rid', key: 'rid' },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (v: string, record: ReferrerItem) => (isUnregistered(record) ? <Tag>未完成注册</Tag> : v),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: '身份',
      dataIndex: 'identity',
      key: 'identity',
      // 未完成注册：identity 是建档默认值 tenant，不展示以免误认为租户
      // identity_other_v1：social 且填了自定义身份时追加展示（如「其他（物业经理）」）
      render: (v: string, record: ReferrerItem) =>
        isUnregistered(record)
          ? <Text type="secondary">—</Text>
          : `${IDENTITY_LABELS[v] ?? v}${record.identityOther ? `（${record.identityOther}）` : ''}`,
    },
    {
      title: '店铺名称',
      dataIndex: 'shopName',
      key: 'shopName',
      render: (v: string | null) => v || '—',
    },
    {
      title: '推荐身份',
      key: 'application',
      render: (_: unknown, r) => {
        const app = r.applications?.[0]
        if (!app) return <Tag>未申请</Tag>
        const s = APPLICATION_STATUS_LABELS[app.status]
        return <Tag color={s?.color ?? 'default'}>{s?.label ?? app.status}</Tag>
      },
    },
    {
      title: '线索数',
      key: 'leads',
      render: (_: unknown, r) => r._count?.leads ?? 0,
    },
    // referrer_share_stats：留位卡数 / 打开次数（服务端排序）
    {
      title: '留位卡数',
      dataIndex: 'cardCount',
      key: 'cardCount',
      sorter: true,
      sortDirections: SORT_CYCLE,
      sortOrder: sortBy === 'cards' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: unknown, r) => r.cardCount ?? 0,
    },
    {
      title: '打开次数',
      dataIndex: 'openCount',
      key: 'openCount',
      sorter: true,
      sortDirections: SORT_CYCLE,
      sortOrder: sortBy === 'opens' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: unknown, r) => r.openCount ?? 0,
    },
    { title: '标签', dataIndex: 'tags', key: 'tags', render: (v?: string | null) => renderTags(v) },
    // thankyou_v1（批次 2）：读后端 include 的嵌套对象（非扁平 rewardTypeName）
    {
      title: '答谢类型',
      key: 'rewardType',
      width: 110,
      render: (_: unknown, r) =>
        r.rewardType?.name ? <Text>{r.rewardType.name}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '绑定方案',
      key: 'rewardPlan',
      width: 200,
      ellipsis: true,
      // referrer_multi_plan：多方案名「、」连接（benefitPlans 映射，回落 rewardPlan.name）
      render: (_: unknown, r) => {
        const names = planNamesOf(r)
        return names ? <Text title={names}>{names}</Text> : <Text type="secondary">-</Text>
      },
    },
    {
      // thankyou_v2（批次 8.2）：资质状态列 —— qualificationFiles 非空→已提交；
      // 类型 needQualification（入驻商家 / 商会）但未传→待提交（红）；其余—
      title: '资质状态',
      key: 'qualification',
      width: 100,
      render: (_: unknown, r) => {
        const files = toFiles(r.qualificationFiles)
        if (files.length > 0) {
          return (
            <Tag color="success">
              已提交（{files.length}）
            </Tag>
          )
        }
        const t = r.rewardType
        const required = !!t?.needQualification || t?.code === MERCHANT_TYPE_CODE || t?.code === 'chamber'
        return required
          ? <Tag color="error">待提交</Tag>
          : <Text type="secondary">—</Text>
      },
    },
    {
      // thankyou_v2：协议编号列（《商家转介合作协议》，入驻商家必填）
      title: '协议编号',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      width: 140,
      ellipsis: true,
      render: (v?: string | null) => (v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortDirections: SORT_CYCLE,
      sortOrder: sortBy === 'createdAt' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: fmtTime,
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <Button size="small" style={{ minHeight: 32 }} onClick={() => openDetail(r)}>
            详情
          </Button>
          {canEditTags && (
            <Button
              size="small"
              style={{ minHeight: 32 }}
              onClick={() => {
                setTagsTarget(r)
                tagsForm.setFieldsValue({ tags: r.tags ?? '' })
              }}
            >
              编辑标签
            </Button>
          )}
          {canEdit && (
            <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>
              编辑资料
            </Button>
          )}
          {canDelete && (
            <Button size="small" danger style={{ minHeight: 32 }} onClick={() => confirmDelete(r)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ]

  // referrer_export：Excel 导出列（与列表列同口径；手机号为服务端按角色脱敏后的值）
  const exportColumns: ExportColumn[] = [
    { title: '推荐码', width: 12, value: (r) => r.rid ?? '' },
    { title: '姓名', width: 12, value: (r) => (isUnregistered(r) ? '未完成注册' : r.name ?? '') },
    { title: '手机号', width: 14, value: (r) => r.phone ?? '' },
    {
      title: '身份',
      width: 16,
      value: (r) => (isUnregistered(r)
        ? '—'
        : `${IDENTITY_LABELS[r.identity] ?? r.identity ?? ''}${r.identityOther ? `（${r.identityOther}）` : ''}`),
    },
    { title: '店铺名称', width: 18, value: (r) => r.shopName ?? '' },
    {
      title: '推荐身份',
      width: 10,
      value: (r) => {
        const app = r.applications?.[0]
        if (!app) return '未申请'
        return APPLICATION_STATUS_LABELS[app.status]?.label ?? app.status
      },
    },
    { title: '线索数', width: 8, type: 'number', value: (r) => r._count?.leads ?? 0 },
    { title: '留位卡数', width: 10, type: 'number', value: (r) => r.cardCount ?? 0 },
    { title: '打开次数', width: 10, type: 'number', value: (r) => r.openCount ?? 0 },
    { title: '标签', width: 18, value: (r) => r.tags ?? '' },
    { title: '答谢类型', width: 12, value: (r) => r.rewardType?.name ?? '' },
    { title: '绑定方案', width: 24, value: (r) => planNamesOf(r) },
    {
      title: '资质状态',
      width: 12,
      value: (r) => {
        const files = toFiles(r.qualificationFiles)
        if (files.length > 0) return `已提交（${files.length}）`
        const t = r.rewardType
        const required = !!t?.needQualification || t?.code === MERCHANT_TYPE_CODE || t?.code === 'chamber'
        return required ? '待提交' : '—'
      },
    },
    { title: '协议编号', width: 18, value: (r) => r.agreementNo ?? '' },
    { title: '注册时间', width: 20, value: (r) => fmtTime(r.createdAt) },
  ]

  return (
    <>
      {/* 第一行：搜索 + 注册状态 */}
      <Space style={{ marginBottom: 8 }} wrap>
        <Input.Search
          allowClear
          placeholder="搜索姓名 / 手机号 / 推荐码"
          style={{ width: 280 }}
          onSearch={(v) => { setKeyword(v); setPage(1) }}
        />
        <Select<RegistrationFilter>
          value={registration}
          options={REGISTRATION_OPTIONS}
          style={{ width: 160 }}
          onChange={(v) => { setRegistration(v); setPage(1) }}
        />
      </Space>
      {/* referrer_share_stats：第二行筛选（身份 / 答谢类型 / 答谢方案 + 重置；drop_referrer_type：已移除推荐人类型） */}
      <Space style={{ marginBottom: 14 }} wrap>
        <Select
          allowClear
          placeholder="身份"
          style={{ width: 130 }}
          options={IDENTITY_OPTIONS}
          value={identityFilter || undefined}
          onChange={(v) => { setIdentityFilter(v ?? ''); setPage(1) }}
        />
        {!benefitFilterDenied && (
          <Select
            allowClear
            placeholder="答谢类型"
            style={{ width: 150 }}
            options={rewardTypeFilterOptions}
            value={rewardTypeIdFilter}
            onChange={(v) => { setRewardTypeIdFilter(v ?? undefined); setPage(1) }}
          />
        )}
        {!benefitFilterDenied && (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="答谢方案"
            style={{ width: 220 }}
            options={benefitPlanFilterOptions}
            value={rewardPlanIdFilter}
            onChange={(v) => { setRewardPlanIdFilter(v ?? undefined); setPage(1) }}
          />
        )}
        <Button onClick={resetFilters}>重置</Button>
        <Button type="primary" ghost disabled={total === 0} onClick={() => setExportOpen(true)}>
          导出
        </Button>
      </Space>
      <Table<ReferrerItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        // antd 翻页时也会触发 Table onChange（action='paginate'），必须按 action 区分，
        // 否则下面的 setPage(1) 会覆盖 pagination.onChange 设置的页码（翻页无效）
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra?.action !== 'sort') return
          const s = Array.isArray(sorter) ? sorter[0] : sorter
          if (s?.field && s.order) {
            const newSortBy = SORTER_FIELD_TO_SORT_BY[String(s.field)]
            const newSortDir = ORDER_TO_SORT_DIR[s.order]
            if (newSortBy && newSortDir) {
              setSortBy(newSortBy)
              setSortDir(newSortDir)
              setPage(1)
            }
          } else {
            // 取消排序：回到默认（列已配 SORT_CYCLE 不再产生取消态，此分支仅作兜底）
            setSortBy('createdAt')
            setSortDir('desc')
            setPage(1)
          }
        }}
        pagination={{
          current: page, pageSize: 20, total,
          showTotal: (t) => `共 ${t} 条`, onChange: (p) => setPage(p),
        }}
      />

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        total={total}
        params={listFilterParams}
        columns={exportColumns}
      />

      <Drawer
        title={`推荐人详情 · ${detail?.name ?? ''}（${detail?.rid ?? ''}）`}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={Math.min(560, window.innerWidth)}
        loading={!detail}
      >
        {detail && (
          <>
            <p><Text type="secondary">手机号：</Text>{detail.phone}</p>
            <p><Text type="secondary">店铺名称：</Text>{detail.shopName || '—'}</p>
            <p>
              <Text type="secondary">身份：</Text>{IDENTITY_LABELS[detail.identity] ?? detail.identity}
              {/* identity_other_v1：social 且填了自定义身份时追加展示 */}
              {detail.identityOther ? `（${detail.identityOther}）` : ''}
              {detail.unit && (
                <>
                  <Text type="secondary" style={{ marginLeft: 16 }}>单元：</Text>{detail.unit}
                </>
              )}
            </p>
            <p><Text type="secondary">标签：</Text>{renderTags(detail.tags)}</p>
            {/* thankyou_v1（批次 2）：详情同步展示答谢类型名 / 绑定方案名 */}
            <p>
              <Text type="secondary">答谢类型：</Text>{detail.rewardType?.name ?? '-'}
              <Text type="secondary" style={{ marginLeft: 16 }}>绑定方案：</Text>
              {planNamesOf(detail) || '-'}
            </p>
            {/* thankyou_v2（批次 8.2）：档案扩字段只读展示（关联商户 / 协议 / 资质） */}
            {(detail.merchant?.name || detail.agreementNo || toFiles(detail.qualificationFiles).length > 0) && (
              <p style={{ fontSize: 13 }}>
                {detail.merchant?.name && (
                  <><Text type="secondary">关联商户：</Text>{detail.merchant.name}</>
                )}
                {detail.agreementNo && (
                  <><Text type="secondary" style={{ marginLeft: 16 }}>协议编号：</Text>{detail.agreementNo}</>
                )}
                {detail.deliveryResponsible && (
                  <><Text type="secondary" style={{ marginLeft: 16 }}>交付责任方：</Text>{detail.deliveryResponsible}</>
                )}
                {toFiles(detail.qualificationFiles).length > 0 && (
                  <>
                    <Text type="secondary" style={{ marginLeft: 16 }}>资质附件：</Text>
                    {toFiles(detail.qualificationFiles).map((u, i) => (
                      <a key={`${u}-${i}`} href={u} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
                        {u.split('/').pop() ?? u}
                      </a>
                    ))}
                  </>
                )}
                {detail.qualificationValidUntil && (
                  <>
                    <Text type="secondary" style={{ marginLeft: 16 }}>资质有效期：</Text>
                    {String(detail.qualificationValidUntil).slice(0, 10)}
                  </>
                )}
              </p>
            )}
            {detail.couponUsageNote && (
              <p style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                <Text type="secondary">券使用说明：</Text>{detail.couponUsageNote}
              </p>
            )}
            <h4 style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 8 }}>贡献统计</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Statistic title="总线索" value={detail.stats.totalLeads} />
              <Statistic title="有效线索" value={detail.stats.validLeads} />
              <Statistic title="成交线索" value={detail.stats.dealtLeads} />
              <Statistic
                title="已发放奖励总额"
                value={fmtMoney(detail.stats.paidRewardTotal)}
                valueStyle={{ color: '#C9A84C' }}
              />
            </div>
            <h4 style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 8, marginTop: 20 }}>推荐身份申请记录</h4>
            {detail.applications && detail.applications.length > 0 ? (
              detail.applications.map((a) => (
                <div key={a.id} style={{ fontSize: 13, padding: '6px 0' }}>
                  <Tag color={APPLICATION_STATUS_LABELS[a.status]?.color ?? 'default'}>
                    {APPLICATION_STATUS_LABELS[a.status]?.label ?? a.status}
                  </Tag>
                  <Text type="secondary">{fmtTime(a.createdAt)}</Text>
                </div>
              ))
            ) : (
              <Text type="secondary">未提交过申请</Text>
            )}
          </>
        )}
      </Drawer>

      <Drawer
        title={`编辑标签 · ${tagsTarget?.name ?? ''}`}
        open={!!tagsTarget}
        onClose={() => setTagsTarget(null)}
        width={Math.min(420, window.innerWidth)}
        extra={
          <Button type="primary" loading={tagsSaving} onClick={saveTags} style={{ minHeight: 44 }}>
            保存
          </Button>
        }
        destroyOnClose
      >
        <Form form={tagsForm} layout="vertical">
          <Form.Item name="tags" label="标签（逗号分隔，≤255 字符）">
            <Input.TextArea rows={3} placeholder="例：重点企业,业主,高产出" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* thankyou_v1（批次 2）：编辑转介人资料抽屉（设计稿 referrer-edit-drawer.html），仅 REFERRER_EDIT 可见入口 */}
      <Drawer
        title={`编辑推荐人资料 · ${editTarget?.name || editTarget?.rid || ''}`}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        width={Math.min(480, window.innerWidth)}
        extra={
          <Button type="primary" loading={editSaving} onClick={saveEdit} style={{ minHeight: 44 }}>
            保存
          </Button>
        }
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '姓名必填' }, { max: 32, message: '姓名不超过 32 字' }]}
          >
            <Input maxLength={32} placeholder="例：张昊天测试" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '手机号必填' },
              { pattern: /^1\d{10}$/, message: '请输入 11 位手机号' },
            ]}
          >
            <Input maxLength={11} placeholder="例：19142530351" />
          </Form.Item>
          <Form.Item name="identity" label="身份" rules={[{ required: true, message: '请选择身份' }]}>
            <Select options={IDENTITY_OPTIONS} placeholder="选择身份" />
          </Form.Item>
          {watchIdentity === 'merchant' && (
            <Form.Item name="shopName" label="店铺名称" rules={[
              { required: true, whitespace: true, message: '请输入店铺名称（必填）' },
              { max: SHOP_NAME_MAX_LEN, message: `店铺名称不超过${SHOP_NAME_MAX_LEN}字`, transform: (v: string) => v?.trim() },
            ]}>
              <Input maxLength={SHOP_NAME_MAX_LEN} placeholder="请输入店铺名称（必填）" />
            </Form.Item>
          )}
          <Form.Item name="unit" label="单元 / 楼层（选填）" rules={[{ max: 32, message: '不超过 32 字' }]}>
            <Input maxLength={32} placeholder="例：T1栋" />
          </Form.Item>
          <Form.Item name="tags" label="标签（逗号分隔，≤255 字符）" rules={[{ max: 255, message: '不超过 255 字' }]}>
            <Input.TextArea rows={2} placeholder="例：VIP,老带新" />
          </Form.Item>
          <Form.Item
            name="rewardTypeId"
            label="答谢类型"
            extra="仅可选已发布（启用）类型；切换类型将自动勾选该类型各业态长期方案（二次确认）"
          >
            <Select
              allowClear
              placeholder="选择答谢类型（可空）"
              options={typeOptions}
              // thankyou_v2（批次 8.2）：改类型改 Modal.confirm 二次确认 + 自动重置兜底方案
              onChange={(v) => handleTypeChange(v ?? null)}
            />
          </Form.Item>
          <Form.Item
            name="rewardPlanIds"
            label="绑定方案（可多选，每个业态一个；客户选择哪个业态就用对应方案，未绑定的业态自动匹配）"
            extra="同一业态再选会替换原方案；改类型后自动勾选新类型各业态长期方案（生效止留空且已发布者）"
            // referrer_multi_plan：同业态互斥（新选替换旧选；通用方案自成一类）
            normalize={(value: number[] | undefined, prev: number[] | undefined) =>
              keepOnePerFormat(value, prev, benefitPlans)}
          >
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={watchTypeId == null ? '不限类型（显示全部可用方案）' : '选择方案（可空，可多选）'}
              options={planOptions}
            />
          </Form.Item>

          {/* ---- thankyou_v2（批次 8.2）转介人档案扩 7 字段（需求 4.5 / 5.2）---- */}
          <Form.Item
            name="merchantId"
            label={`关联商户${isMerchantType ? '（入驻商家必填）' : '（选填）'}`}
            extra="指向商户档案，决定券出自哪家店；面值由方案分档子表按成交面积套档"
            rules={isMerchantType ? [{ required: true, message: '入驻商家必须选择关联商户' }] : []}
          >
            <Select allowClear showSearch optionFilterProp="label" placeholder="选择商户" options={merchantOptions} />
          </Form.Item>
          <Form.Item
            name="agreementNo"
            label={`商家协议编号${isMerchantType ? '（入驻商家必填）' : '（选填）'}`}
            extra="指向已签《商家转介合作协议》；未签不允许绑定入驻商家类型（后端 400）"
            rules={[
              ...(isMerchantType ? [{ required: true, message: '入驻商家必填协议编号' }] : []),
              { max: 64, message: '协议编号不超过 64 字' },
            ]}
          >
            <Input maxLength={64} placeholder="例：SJ-2026-018" />
          </Form.Item>
          <Form.Item
            name="couponUsageNote"
            label={`券使用说明${isMerchantType ? '（入驻商家必填）' : '（选填）'}`}
            extra="门店名称地址、券有效期、使用限制；未写入的限制一律不得对客户主张"
            rules={[
              ...(isMerchantType ? [{ required: true, message: '入驻商家必填券使用说明' }] : []),
              { max: 1000, message: '不超过 1000 字' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount placeholder="例：XX 店（A座 1F），面值 10,000 / 20,000 元餐饮代金券，自发放之日起 6 个月内有效，不与其他优惠叠加"
            />
          </Form.Item>
          {isMerchantType && (
            <Form.Item label="礼遇交付责任方（只读）">
              <Input readOnly value={DELIVERY_RESPONSIBLE_TEXT} style={{ background: '#F5F7FA' }} />
            </Form.Item>
          )}
          <Form.Item
            name="qualificationFiles"
            label={`资质材料附件${needQualification ? '（必传：图片 / PDF，可多张）' : '（选填）'}`}
            extra="走 POST /admin/uploads/document；改类型时不上传不允许保存（后端 400 双保险）"
          >
            <DocumentUploadList
              disabled={!canEdit}
              hint="支持 JPG / PNG / GIF / PDF，单个 ≤ 10MB，可多选；已上传项点击文件名可预览"
            />
          </Form.Item>
          <Form.Item
            name="qualificationValidUntil"
            label="资质有效期（选填）"
            extra="商户退租、商会会员到期触发复核"
          >
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          {editTarget && (editTarget.qualificationReviewer || editTarget.qualificationReviewedAt) && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              资质审核人：{editTarget.qualificationReviewer ?? '—'}
              {' · '}审核时间：{fmtTime(editTarget.qualificationReviewedAt)}
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            仅超管 / 管理员可见此入口；保存即生效（后端同步校验类型/方案启用状态、归属关系、
            资质与入驻商家必填项）。
          </Text>
        </Form>
      </Drawer>
    </>
  )
}

export default ReferrersPage
